import { trpc } from "@/api/trpc";
import { Button } from "@/components/ui/button";
import { useRoleImpersonationStore } from "@/shared/stores/role-impersonation";
import { ROLE_LABELS_CS, USER_ROLES, type UserRole, asUserRole } from "@template/shared";

/**
 * Persistent banner that an admin sees whenever they are viewing the UI
 * as a lower-privilege role. Rendered at the top of the dashboard layout.
 *
 * - Invisible for non-admins and for admins with no impersonation active.
 * - Offers a role picker that admins can use to preview any role.
 *
 * Zero security value — the API authorises against the real cookie. This
 * exists so admins never forget they are in "view as" mode.
 */
export function ImpersonationBanner() {
  const { data } = trpc.auth.getSession.useQuery();
  const realRole = asUserRole(data?.profile?.role);
  const impersonated = useRoleImpersonationStore((s) => s.impersonatedRole);
  const setImpersonated = useRoleImpersonationStore((s) => s.setImpersonatedRole);
  const clear = useRoleImpersonationStore((s) => s.clear);

  // Only admins get the control. Non-admin sessions see nothing.
  if (realRole !== "admin") return null;

  return (
    <section
      className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-secondary px-4 py-2 text-sm text-secondary-foreground"
      data-testid="impersonation-banner"
      aria-label="Náhled v jiné roli"
    >
      <div className="flex items-center gap-2">
        <span className="font-medium">Zobrazit jako</span>
        <select
          className="h-8 rounded-md border border-input bg-background px-2 text-sm"
          value={impersonated ?? "admin"}
          onChange={(e) => {
            const next = e.target.value as UserRole;
            if (next === realRole) {
              clear();
            } else {
              setImpersonated(next);
            }
          }}
          data-testid="impersonation-banner-role-select"
          aria-label="Role pro náhled"
        >
          {USER_ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS_CS[r]}
            </option>
          ))}
        </select>
        {impersonated && (
          <span className="text-muted-foreground">
            (API vás stále vidí jako <strong>{ROLE_LABELS_CS.admin}</strong>)
          </span>
        )}
      </div>
      {impersonated && (
        <Button size="sm" variant="outline" onClick={clear} data-testid="impersonation-banner-exit">
          Ukončit náhled
        </Button>
      )}
    </section>
  );
}
